import { create } from 'zustand'

export interface AuthUser {
  username: string
  role: 'admin' | 'user'
  credits: number
}

interface AuthState {
  /** null = 비로그인, undefined = 아직 조회 전 */
  user: AuthUser | null | undefined
  refresh: () => Promise<void>
  login: (username: string, password: string) => Promise<string | null>
  register: (username: string, password: string, invite: string) => Promise<string | null>
  logout: () => Promise<void>
  /** /api/chat 응답에 실려 온 잔액으로 즉시 갱신 */
  setCredits: (credits: number) => void
}

async function post(path: string, body?: unknown): Promise<{ ok?: boolean; user?: AuthUser; error?: string }> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  return res.json().catch(() => ({ error: '응답을 해석할 수 없습니다.' }))
}

export const useAuth = create<AuthState>((set) => ({
  user: undefined,

  refresh: async () => {
    try {
      const res = await fetch('/api/auth/me')
      const data = await res.json()
      set({ user: data.user ?? null })
    } catch {
      set({ user: null })
    }
  },

  login: async (username, password) => {
    const data = await post('/api/auth/login', { username, password })
    if (data.user) {
      set({ user: data.user })
      return null
    }
    return data.error ?? '로그인에 실패했습니다.'
  },

  register: async (username, password, invite) => {
    const data = await post('/api/auth/register', { username, password, invite })
    if (data.user) {
      set({ user: data.user })
      return null
    }
    return data.error ?? '가입에 실패했습니다.'
  },

  logout: async () => {
    await post('/api/auth/logout').catch(() => undefined)
    set({ user: null })
  },

  setCredits: (credits) =>
    set((s) => (s.user ? { user: { ...s.user, credits } } : s)),
}))
